import React, { useEffect, useMemo, useState } from "react";
import { useLanguage } from "@src/context/LanguageContext";
import * as HELPER from "@src/config/helper";

interface Props {
  lead_tenure_data: any;
  onChange?: (changedData: any) => void;
  onErrorChange?: (hasError: boolean) => void;
  disabled?: boolean;
}

const DepreciationRates = (props: Props) => {
  const { lead_tenure_data, onChange, onErrorChange, disabled = false } = props;
  const { translations } = useLanguage();
  const shouldShowDepreciation = Array.isArray(lead_tenure_data)
    && lead_tenure_data.some((el: any) => Number(el?.tenure) > 1);

  const fields = useMemo<Array<{ key: string; label: string; value: number }>>(() => {
    if (!shouldShowDepreciation) {
      return [];
    }

    return (
      lead_tenure_data?.map((el: any) => {
        return {
          key: `year_${el.tenure}_${el.insurance_type_name?.toLowerCase()}`,
          label: `Year ${el.tenure} - ${el.insurance_type_name}`,
          value: Number(el.dep_rate ?? 40)
        };
      }) || []
    );
  }, [lead_tenure_data, shouldShowDepreciation]);

  const initialValues = useMemo(() => {
    return fields.reduce(
      (acc: Record<string, string>, cur: any) => ({ ...acc, [cur.key]: String(cur.value ?? "") }),
      {} as Record<string, string>
    );
  }, [fields]);

  const [values, setValues] = useState<Record<string, string>>(initialValues);
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});

  useEffect(() => {
    if (!shouldShowDepreciation) {
      setErrors({});
      onErrorChange?.(false);
    }
  }, [shouldShowDepreciation, onErrorChange]);

  useEffect(() => {
    onErrorChange?.(Object.keys(errors).length > 0);
  }, [errors, onErrorChange]);

  if (!shouldShowDepreciation) {
    return null;
  }

  const parsePercent = (raw: string) => {
    const cleaned = String(raw ?? "").trim().replace(/%/g, "");
    if (!cleaned) return null;
    const num = Number(cleaned);
    return Number.isFinite(num) ? num : null;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, key: string) => {
    if (disabled) return;

    const nextValue = e.target.value;
    const cleaned = String(nextValue ?? "").trim().replace(/%/g, "");
    const num = parsePercent(nextValue);

    if (cleaned && cleaned.includes(".")) {
      const decimalPart = cleaned.split(".")[1] ?? "";
      if (decimalPart.length > 4) {
        setErrors((prev) => ({ ...prev, [key]: translations?.commonMsg?.MAX_4D}));
        return;
      }

      if (num !== null && !HELPER.isValidDecimal(num)) {
        setErrors((prev) => ({ ...prev, [key]: translations?.commonMsg?.MAX_4D}));
        return;
      }
    }

    if (num !== null && num > 100) return;

    if (num === null || String(num) === "" || num === undefined) {
      setErrors((prev) => ({
        ...prev,
        [key]: translations?.commonMsg?.FIELD_CANNOT_BE_BLANK,
      }));
    }
    else if (num !== null && (num < 40 || num > 100)) {
      setErrors((prev) => ({ ...prev, [key]: translations?.Insurance?.DEP_RATE_ERROR }));
    } else {
      setErrors((prev) => {
        if (!prev[key]) return prev;
        const updated = { ...prev };
        delete updated[key];
        return updated;
      });
    }

    setValues((prev) => {
      const updated: Record<string, string> = { ...prev, [key]: nextValue };

      if (typeof onChange === "function" && Array.isArray(lead_tenure_data)) {
        const updatedLeadTenureData = lead_tenure_data.map((row: any) => {
          const rowKey = `year_${row.tenure}_${row.insurance_type_name?.toLowerCase()}`;
          const updatedRate = parsePercent(updated[rowKey]);
          return {
            insurance_type: row.insurance_type_id,
            dep_rate: updatedRate ?? row.dep_rate ?? 40,
            tenure: row.tenure,
            insurance_type_name:row.insurance_type_name
          };
        });
        onChange(updatedLeadTenureData);
      }

      return updated;
    });
  };

  return (
    <div className="depr-container">
      <h3 className="depr-heading">
        {translations?.Insurance?.DEP_RATE_TITLE}
      </h3>

      {fields.map((item, index) => (
        <div key={item.key} className="depr-row">
          <span>{item.label}</span>
          <div className="depr-input-container">
            <input
              type="text"
              value={values[item.key]}
              className="depr-input"
              disabled={index === 0 ? true : disabled}
              onChange={(e) => handleChange(e, item.key)}
            />
            {errors[item.key] ? <span className="error-txt">{errors[item.key]}</span> : null}
          </div>
        </div>
      ))}
    </div>
  );
};

export default DepreciationRates;
